import { FilterChangedEvent, GridApi, GridState, StateUpdatedEvent } from 'ag-grid-community';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { viewTypesWithoutFundFilter } from '../../../../components/Layout/Header/Header';
import { ViewModel } from '../../../../models/view.model';
import { DefaultIgnoreList, doOnGridStateUpdated } from '../../../../util/ag-grid-utils';
import { useColumnMetaMap } from '../../hooks/useColumns/useColumns';
import { useUpdateSelectedView } from '../../hooks/useViewActions';
import { portfolioAllFundsSelectedStateJ } from '../../state/FundFilterStateJ';

export const pfGridApiAtom = atom<GridApi | null>(null);

export const pfGridStateAtom = atom<GridState | null>(null);

/** The text has to outlive a Flat / Tree remount, but this atom is module-global and every
 * `SharedTable` mount reads it — hence the owning view id. */
export interface IViewQuickFilter {
  viewId: number;
  text: string;
}

export const quickFilterAtom = atom<IViewQuickFilter | undefined>(undefined);

export function quickFilterTextFor(
  quickFilter: IViewQuickFilter | undefined,
  viewId: number
): string | undefined {
  return quickFilter?.viewId === viewId ? quickFilter.text : undefined;
}

export function usePfGridState(viewId?: number) {
  const setPfGridState = useSetAtom(pfGridStateAtom);
  const setPfGridApi = useSetAtom(pfGridApiAtom);
  const setQuickFilter = useSetAtom(quickFilterAtom);
  const updateSelectedView = useUpdateSelectedView();

  // FD-2977: keep the local atom mirror unguarded (filter chips need the full
  // state including scroll/selection), but route the persisted view update
  // through doOnGridStateUpdated so we skip `gridInitializing`, `scroll`,
  // `cellSelection`, and `focusedCell` events. Without the guard, every scroll
  // or cell click PUTs the view — the same loop the removed
  // PROGRAMMATIC_COLUMN_EVENT_SOURCES guard was defending against.
  const persistGridState = useMemo(
    () =>
      doOnGridStateUpdated((state) => {
        updateSelectedView({ gridState: state });
      }, DefaultIgnoreList),
    [updateSelectedView]
  );

  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      setPfGridState(event.state);
      persistGridState(event);
    },
    [setPfGridState, persistGridState]
  );

  const resetGridState = useCallback(() => {
    setPfGridState(null);
    setPfGridApi(null);
  }, [setPfGridState, setPfGridApi]);

  // quick filter is not stored in grid state, so we need onFilterChange
  const checkQuickFilter = useCallback(
    (event: FilterChangedEvent) => {
      if (viewId == null) {
        return;
      }
      const text = event.api.isQuickFilterPresent() ? event.api.getQuickFilter() : undefined;
      setQuickFilter(text ? { viewId, text } : undefined);
    },
    [setQuickFilter, viewId]
  );

  return { onStateUpdated, resetGridState, checkQuickFilter };
}

export function usePfFilterState(view: ViewModel) {
  const allFundsSelected = useAtomValue(portfolioAllFundsSelectedStateJ);
  const quickFilterText = quickFilterTextFor(useAtomValue(quickFilterAtom), view.id);
  const columnNameToDisplayName = useColumnMetaMap();
  const { filter } = useAtomValue(pfGridStateAtom) ?? {};
  const { viewType } = view;

  const appliedGridFilters = useMemo(() => {
    let searchText: string | undefined;
    if (quickFilterText) {
      searchText = `Search: "${quickFilterText}"`;
    }
    const result = searchText ? [searchText] : [];
    return Object.keys(filter?.filterModel ?? {}).reduce((acc, key) => {
      const displayName = columnNameToDisplayName.get(key);
      acc.push(displayName ?? key);
      return acc;
    }, result);
  }, [filter, columnNameToDisplayName, quickFilterText]);

  const appliedFilters = useMemo(() => {
    if (allFundsSelected || viewTypesWithoutFundFilter.has(viewType)) {
      return appliedGridFilters;
    } else {
      return ['Fund', ...appliedGridFilters];
    }
  }, [allFundsSelected, appliedGridFilters, viewType]);

  return appliedFilters;
}
